import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, ArrowLeft, Calendar, CheckSquare, GitBranch,
  FileText, Folder, Clock, User, Loader2, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ITEM_TYPES = [
  { id: 'meeting', label: 'Meetings', icon: Calendar, color: 'text-blue-600' },
  { id: 'task', label: 'Tasks', icon: CheckSquare, color: 'text-amber-600' },
  { id: 'decision', label: 'Decisions', icon: GitBranch, color: 'text-purple-600' },
  { id: 'file', label: 'Files', icon: Folder, color: 'text-emerald-600' }
];

const Repository = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [query, setQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(['meeting', 'task', 'decision', 'file']);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    
    try {
      setLoading(true);
      setSearched(true);
      
      const res = await axios.post(
        `${API}/api/meeting-intelligence/repository/search?token=${token}`,
        {
          query,
          item_types: selectedTypes
        }
      );
      
      setResults(res.data.results);
    } catch (err) {
      console.error('Error searching:', err);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const getTotalResults = () => {
    if (!results) return 0;
    return Object.values(results).reduce((sum, arr) => sum + arr.length, 0);
  };

  return (
    <div className="p-6 space-y-6" data-testid="repository-search">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/meeting-intelligence')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Search className="h-7 w-7 text-purple-600" />
            Knowledge Repository
          </h1>
          <p className="text-slate-500 mt-1">Search across all meetings, tasks, decisions, and files</p>
        </div>
      </div>

      {/* Search Box */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Search for meetings, tasks, decisions, files..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-12 h-12 text-lg"
                />
              </div>
              <Button 
                onClick={handleSearch}
                className="h-12 px-8 bg-purple-600 hover:bg-purple-700"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>
            
            {/* Type Filters */}
            <div className="flex items-center gap-6">
              <span className="text-sm text-slate-500 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filter by:
              </span>
              {ITEM_TYPES.map(type => (
                <label 
                  key={type.id}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedTypes.includes(type.id)}
                    onCheckedChange={() => toggleType(type.id)}
                  />
                  <type.icon className={`h-4 w-4 ${type.color}`} />
                  <span className="text-sm">{type.label}</span>
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {searched && (
        <div className="space-y-6">
          {/* Results Summary */}
          <div className="flex items-center justify-between">
            <p className="text-slate-600">
              Found <strong>{getTotalResults()}</strong> results for "<strong>{query}</strong>"
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : results && getTotalResults() === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium text-slate-600">No results found</p>
                <p className="text-sm text-slate-500 mt-1">Try different keywords or filters</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Meetings Results */}
              {results?.meetings?.length > 0 && selectedTypes.includes('meeting') && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      Meetings
                      <Badge variant="secondary">{results.meetings.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {results.meetings.map(meeting => (
                      <div 
                        key={meeting.id}
                        className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                        onClick={() => navigate(`/meeting-intelligence/meetings/${meeting.id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{meeting.title}</p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {meeting.scheduled_start ? new Date(meeting.scheduled_start).toLocaleDateString() : 'Not scheduled'}
                              </span>
                              <Badge variant="outline" className="capitalize">{meeting.meeting_type}</Badge>
                              <Badge variant={meeting.status === 'completed' ? 'default' : 'outline'} className="capitalize">
                                {meeting.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {meeting.description && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">{meeting.description}</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Tasks Results */}
              {results?.tasks?.length > 0 && selectedTypes.includes('task') && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CheckSquare className="h-5 w-5 text-amber-600" />
                      Tasks
                      <Badge variant="secondary">{results.tasks.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {results.tasks.map(task => (
                      <div 
                        key={task.id}
                        className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                        onClick={() => navigate(`/meeting-intelligence/tasks/${task.id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{task.title}</p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              {task.owner_name && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {task.owner_name}
                                </span>
                              )}
                              <Badge variant={
                                task.priority === 'high' ? 'destructive' :
                                task.priority === 'medium' ? 'default' : 'secondary'
                              } className="text-xs">
                                {task.priority}
                              </Badge>
                              <Badge variant="outline" className="capitalize">{task.status?.replace('_', ' ')}</Badge>
                            </div>
                          </div>
                        </div>
                        {task.description && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">{task.description}</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Decisions Results */}
              {results?.decisions?.length > 0 && selectedTypes.includes('decision') && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <GitBranch className="h-5 w-5 text-purple-600" />
                      Decisions
                      <Badge variant="secondary">{results.decisions.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {results.decisions.map(decision => (
                      <div 
                        key={decision.id}
                        className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <p className="text-slate-900 dark:text-white">{decision.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          {decision.decided_by && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {decision.decided_by}
                            </span>
                          )}
                          <Badge variant={
                            decision.impact === 'high' ? 'destructive' :
                            decision.impact === 'medium' ? 'default' : 'secondary'
                          } className="text-xs">
                            {decision.impact} impact
                          </Badge>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(decision.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Files Results */}
              {results?.files?.length > 0 && selectedTypes.includes('file') && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Folder className="h-5 w-5 text-emerald-600" />
                      Files
                      <Badge variant="secondary">{results.files.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {results.files.map(file => (
                      <div 
                        key={file.id}
                        className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-4"
                      >
                        <div className="w-10 h-10 rounded bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{file.file_name}</p>
                          <p className="text-sm text-slate-500">
                            Uploaded by {file.uploaded_by_name} • {new Date(file.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!searched && (
        <div className="text-center py-16">
          <Search className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-medium text-slate-600 mb-2">Search Your Knowledge Base</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Find meetings, tasks, decisions, and documents across your organization. 
            Start typing to search.
          </p>
        </div>
      )}
    </div>
  );
};

export default Repository;
